import { Create, useForm } from '@refinedev/antd'
import { useCustom, useInvalidate } from '@refinedev/core'
import { Row, Col, Space, Button, message, Card, Typography, Alert } from 'antd'
import { SendOutlined, SaveOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useState, useMemo } from 'react'
import dayjs, { Dayjs } from 'dayjs'

import {
  MediaUploader,
  PlatformSelector,
  CaptionEditor,
  SchedulePicker,
  PlatformPreview,
  SocialPlatform,
} from '@/components/social-media'

const { Text } = Typography

interface SocialAccount {
  id: string
  platform: SocialPlatform
  accountName: string
  accountHandle: string | null
  status: 'ACTIVE' | 'EXPIRED' | 'REVOKED' | 'ERROR'
}

interface CreateSocialPostForm {
  content: string
  platforms: SocialPlatform[]
  scheduledAt?: string
  mediaUrls?: string[]
  mediaTypes?: string[]
}

// Platform character limits
const CHAR_LIMITS: Record<SocialPlatform, number> = {
  INSTAGRAM: 2200,
  FACEBOOK: 63206,
  TIKTOK: 2200,
  TWITTER: 280,
  YOUTUBE: 5000,
}

export default function SocialMediaCreatePage() {
  const navigate = useNavigate()
  const invalidate = useInvalidate()
  const { id } = useParams()
  const isEdit = !!id

  // Form state
  const [content, setContent] = useState('')
  const [platforms, setPlatforms] = useState<SocialPlatform[]>([])
  const [scheduledAt, setScheduledAt] = useState<Dayjs | null>(null)
  const [mediaKey, setMediaKey] = useState<string | undefined>()
  const [mediaInfo, setMediaInfo] = useState<{ file: File; type: 'video' | 'image'; duration?: number; url: string; thumbnailUrl: string } | null>(null)
  const [saving, setSaving] = useState(false)

  // Fetch connected accounts
  const { query: accountsQuery } = useCustom<SocialAccount[]>({
    url: '/admin/social-media/accounts',
    method: 'get',
  })

  const accounts = useMemo(() => {
    const data = accountsQuery.data?.data
    if (Array.isArray(data)) return data
    if (data && typeof data === 'object' && 'data' in data) {
      return (data as { data: SocialAccount[] }).data
    }
    return []
  }, [accountsQuery.data])

  // Calculate minimum character limit across selected platforms
  const minCharLimit = useMemo(() => {
    if (platforms.length === 0) return 2200
    return Math.min(...platforms.map((p) => CHAR_LIMITS[p]))
  }, [platforms])

  // Form validation
  const canSave = content.trim().length > 0 && platforms.length > 0
  const canPublish = canSave && mediaKey
  const isOverLimit = content.length > minCharLimit

  // Handle save as draft
  const handleSaveDraft = async () => {
    if (!canSave) return

    setSaving(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

      const body: CreateSocialPostForm = {
        content,
        platforms,
        mediaUrls: mediaKey ? [mediaKey] : [],
        mediaTypes: mediaInfo?.file.type ? [mediaInfo.file.type] : [],
      }

      if (scheduledAt) {
        body.scheduledAt = scheduledAt.toISOString()
      }

      const response = await fetch(`${API_URL}/admin/social-media/posts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Cuvanje nije uspelo')
      }

      const result = await response.json()
      message.success(scheduledAt ? 'Post je zakazan' : 'Nacrt je sacuvan')
      navigate(`/social-media/${result.id}`)
    } catch (error) {
      message.error(`Greska: ${error instanceof Error ? error.message : 'Neuspelo'}`)
    } finally {
      setSaving(false)
    }
  }

  // Handle publish now
  const handlePublishNow = async () => {
    if (!canPublish) return

    setSaving(true)
    try {
      const token = localStorage.getItem('admin_access_token')
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

      // First create the post
      const createResponse = await fetch(`${API_URL}/admin/social-media/posts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content,
          platforms,
          mediaUrls: mediaKey ? [mediaKey] : [],
          mediaTypes: mediaInfo?.file.type ? [mediaInfo.file.type] : [],
        }),
      })

      if (!createResponse.ok) {
        throw new Error('Kreiranje posta nije uspelo')
      }

      const post = await createResponse.json()

      // Then publish it
      const publishResponse = await fetch(`${API_URL}/admin/social-media/posts/${post.id}/publish`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!publishResponse.ok) {
        throw new Error('Objavljivanje nije uspelo')
      }

      message.success('Post je poslat na objavljivanje')
      navigate(`/social-media/${post.id}`)
    } catch (error) {
      message.error(`Greska: ${error instanceof Error ? error.message : 'Neuspelo'}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Create
      title={isEdit ? 'Izmeni post' : 'Novi post'}
      breadcrumb={null}
      headerButtons={
        <Space>
          <Button onClick={() => navigate('/social-media')}>Otkazi</Button>
          <Button
            icon={<SaveOutlined />}
            onClick={handleSaveDraft}
            loading={saving}
            disabled={!canSave}
          >
            {scheduledAt ? 'Zakazi' : 'Sacuvaj nacrt'}
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handlePublishNow}
            loading={saving}
            disabled={!canPublish}
          >
            Objavi odmah
          </Button>
        </Space>
      }
      footerButtons={null}
    >
      <Row gutter={24}>
        {/* Left Column - Content */}
        <Col xs={24} lg={10}>
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            {/* Media Upload */}
            <Card title="Medija" size="small">
              <MediaUploader
                value={mediaKey}
                onChange={setMediaKey}
                onMediaInfo={(info) => setMediaInfo(info ? { file: info.file, type: info.type, duration: info.duration, url: info.url, thumbnailUrl: info.thumbnailUrl } : null)}
                maxVideoDuration={180}
                acceptImages={true}
                acceptVideos={true}
              />
            </Card>

            {/* Caption */}
            <CaptionEditor
              value={content}
              onChange={setContent}
              selectedPlatforms={platforms}
              minCharLimit={minCharLimit}
            />
          </Space>
        </Col>

        {/* Center Column - Preview */}
        <Col xs={24} lg={8}>
          <PlatformPreview
            videoUrl={mediaInfo?.type === 'video' ? mediaInfo?.url : undefined}
            imageUrl={mediaInfo?.type === 'image' ? mediaInfo?.url : undefined}
            thumbnailUrl={mediaInfo?.thumbnailUrl}
            content={content}
            selectedPlatforms={platforms}
            accountName="PerfectaMed"
            accountHandle="perfectamed"
          />
        </Col>

        {/* Right Column - Settings */}
        <Col xs={24} lg={6}>
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            {/* Platform Selection */}
            <PlatformSelector
              value={platforms}
              onChange={setPlatforms}
              connectedAccounts={accounts}
              characterCount={content.length}
            />

            {/* Schedule */}
            <SchedulePicker
              value={scheduledAt}
              onChange={setScheduledAt}
            />

            {/* Validation warnings */}
            {isOverLimit && (
              <Alert
                message="Tekst je predugacak"
                description={`Tekst prelazi limit od ${minCharLimit} karaktera za izabrane platforme.`}
                type="warning"
                showIcon
              />
            )}

            {platforms.length > 0 && !mediaKey && (
              <Alert
                message="Medija nije dodata"
                description="Dodajte sliku ili video da biste mogli da objavite post."
                type="info"
                showIcon
              />
            )}

            {/* Connected platforms count */}
            <Card size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Povezano {accounts.filter((a) => a.status === 'ACTIVE').length} od 5 naloga
                </Text>
                {platforms.length > 0 && (
                  <Text style={{ fontSize: 12 }}>
                    Izabrano {platforms.length} platform{platforms.length === 1 ? 'a' : 'e'}
                  </Text>
                )}
              </Space>
            </Card>
          </Space>
        </Col>
      </Row>
    </Create>
  )
}
